"use strict";
/**
 * HUMAN-READABLE ORDER NUMBERS.
 *
 * `ord_bfdda6e1d002b5cf7b152904` is a UUID — unreadable, unsayable, and
 * impossible to check when a merchant reads it down the phone to support two
 * years after the order shipped.
 *
 * THIS IS NOT THE KEY. `orderId` stays exactly as it is: every unit row,
 * envelope, invoice and audit row references it, and re-keying would orphan
 * years of history. `orderNo` is a second, human-facing identifier stamped
 * alongside it.
 *
 * WHY A CHECK DIGIT. These get read aloud during a support call, a warranty
 * claim, a collection. The Damm algorithm catches every single-digit error and
 * every adjacent transposition — the two mistakes people actually make — for
 * the cost of one extra character.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ORDER_NUMBER_BLOCK_SIZE = exports.isOrderNumber = exports.ORDER_NUMBER_START = exports.ORDER_NUMBER_PREFIX = exports.dammIsValid = void 0;
exports.dammCheckDigit = dammCheckDigit;
exports.formatOrderNumber = formatOrderNumber;
exports.parseOrderNumber = parseOrderNumber;
exports.displayOrderNumber = displayOrderNumber;
exports.blockFromCounter = blockFromCounter;
exports.takeFromBlock = takeFromBlock;
/**
 * Damm's quasigroup. Chosen over Luhn because Luhn misses the 09↔90
 * transposition, and "RB-1000904382" vs "RB-1000094382" is exactly the kind of
 * pair a support agent would pull up without noticing.
 */
const DAMM = [
    [0, 3, 1, 7, 5, 9, 8, 6, 4, 2],
    [7, 0, 9, 2, 1, 5, 4, 8, 6, 3],
    [4, 2, 0, 6, 8, 7, 1, 3, 5, 9],
    [1, 7, 5, 0, 9, 8, 3, 4, 2, 6],
    [6, 1, 2, 3, 0, 4, 5, 9, 7, 8],
    [3, 6, 7, 4, 2, 0, 9, 5, 8, 1],
    [5, 8, 6, 9, 7, 2, 0, 1, 3, 4],
    [8, 9, 4, 5, 3, 6, 2, 0, 1, 7],
    [9, 4, 3, 8, 6, 1, 7, 2, 0, 5],
    [2, 5, 8, 1, 4, 3, 6, 7, 9, 0],
];
/** The check digit for a run of digits. Appending it makes the whole string check to 0. */
function dammCheckDigit(digits) {
    let interim = 0;
    for (const ch of digits) {
        const d = ch.charCodeAt(0) - 48;
        if (d < 0 || d > 9)
            throw new Error(`not a digit: ${ch}`);
        interim = DAMM[interim][d];
    }
    return interim;
}
/** Does this run of digits (INCLUDING its check digit) check out? */
const dammIsValid = (digitsWithCheck) => {
    try {
        return dammCheckDigit(digitsWithCheck) === 0;
    }
    catch {
        return false;
    }
};
exports.dammIsValid = dammIsValid;
/** The house prefix. Short, unmistakable, and it says whose order this is. */
exports.ORDER_NUMBER_PREFIX = 'RB';
/**
 * Where the sequence starts.
 *
 * Not 1. An order numbered RB-10 tells a merchant they are the tenth customer
 * this company has ever had, which is true and unhelpful. Six digits also
 * leaves the format visually stable for the first several million orders.
 */
exports.ORDER_NUMBER_START = 100000;
/**
 * `RB-` + sequence + check digit.
 *
 * SCALES TO BILLIONS by simply getting longer — there is no fixed width to run
 * out of, which is what happens to a zero-padded `RTA-######` at one million.
 * At ten billion orders this is `RB-100000000004` and still reads as a number.
 */
function formatOrderNumber(sequence) {
    if (!Number.isInteger(sequence) || sequence < 0) {
        throw new Error(`order sequence must be a non-negative integer, got ${sequence}`);
    }
    if (sequence > Number.MAX_SAFE_INTEGER) {
        throw new Error('order sequence has outgrown a safe integer');
    }
    const body = String(sequence);
    return `${exports.ORDER_NUMBER_PREFIX}-${body}${dammCheckDigit(body)}`;
}
/** The sequence back out of a number, or null if it does not check out. */
function parseOrderNumber(orderNo) {
    const m = /^([A-Z]{2})-(\d{2,})$/.exec(String(orderNo || '').trim().toUpperCase());
    if (!m || m[1] !== exports.ORDER_NUMBER_PREFIX)
        return null;
    if (!(0, exports.dammIsValid)(m[2]))
        return null;
    return Number(m[2].slice(0, -1));
}
const isOrderNumber = (s) => parseOrderNumber(s) !== null;
exports.isOrderNumber = isOrderNumber;
/**
 * Grouped for reading aloud: `RB-100 000 438 2`.
 *
 * Display only — never stored, never compared, and `parseOrderNumber` accepts
 * the ungrouped form because that is what people type back in.
 */
function displayOrderNumber(orderNo) {
    const m = /^([A-Z]{2})-(\d+)$/.exec(String(orderNo || '').trim().toUpperCase());
    if (!m)
        return orderNo;
    return `${m[1]}-${m[2].replace(/(\d{3})(?=\d)/g, '$1 ')}`;
}
/**
 * WHY BLOCKS, not a single counter.
 *
 * The obvious implementation is one DynamoDB row incremented with ADD, which
 * is what `RTA-######` does. That row is a single item, and a single item caps
 * at roughly 1,000 writes a second — it becomes the ceiling on order creation
 * long before a billion orders, and every order in the system serialises on it.
 *
 * Instead a caller claims a BLOCK and hands numbers out from memory: one
 * counter write per `ORDER_NUMBER_BLOCK_SIZE` orders. A container that dies
 * mid-block leaves a gap, which is what gaps in commercial order numbers have
 * always been and costs nothing.
 */
exports.ORDER_NUMBER_BLOCK_SIZE = 1000;
/** A block from a counter's post-increment value. */
function blockFromCounter(counterAfterAdd, size = exports.ORDER_NUMBER_BLOCK_SIZE) {
    const to = exports.ORDER_NUMBER_START + counterAfterAdd;
    const from = to - size;
    return { from, to, next: from };
}
/** The next number in a block, or null when it is spent and a new one is needed. */
function takeFromBlock(block) {
    if (block.next >= block.to)
        return null;
    return {
        orderNo: formatOrderNumber(block.next),
        block: { ...block, next: block.next + 1 },
    };
}
//# sourceMappingURL=orderNumber.js.map